import { CSSProperties, PropsWithChildren } from 'react';
import { SidebarPinStateProvider } from '@backstage/core-components';
import { useAnalytics } from '@backstage/core-plugin-api';
import { SearchModalProvider } from '@backstage/plugin-search';
import { GlobalSearchShortcut } from '../GlobalSearchShortcut';
import { AppSidebar } from './AppSidebar';
import { useSidebarNarrow } from './useSidebarNarrow';

// TechDocs positions its own navigation from the app sidebar's width.
const TECHDOCS_OFFSETS = {
  '--techdocs-sidebar-closed-offset-pinned': '200px',
  '--techdocs-sidebar-closed-offset-collapsed': '72px',
} as CSSProperties;

/**
 * Page layout with the BUI sidebar. The legacy sidebar's pin state maps to
 * the expanded mode, so upstream pages that read it keep their layout.
 */
export const AppShell = ({ children }: PropsWithChildren<{}>) => {
  const analytics = useAnalytics();
  const reportMode = (next: boolean, source: 'logo' | 'keyboard') => {
    const mode = next ? 'narrow' : 'expanded';
    analytics.captureEvent('sidebar_mode_change', mode, {
      attributes: { variant: 'v2', mode, source },
    });
  };
  const { narrow, setNarrow } = useSidebarNarrow(next =>
    reportMode(next, 'keyboard'),
  );

  return (
    <SidebarPinStateProvider
      value={{
        isPinned: !narrow,
        isMobile: false,
        toggleSidebarPinState: () => setNarrow(!narrow),
      }}
    >
      <div className="make-shell" style={TECHDOCS_OFFSETS}>
        <SearchModalProvider>
          <GlobalSearchShortcut />
          <AppSidebar
            narrow={narrow}
            onNarrowChange={next => {
              setNarrow(next);
              reportMode(next, 'logo');
            }}
          />
        </SearchModalProvider>
        <div className="make-shell__content">{children}</div>
      </div>
    </SidebarPinStateProvider>
  );
};
